import {
  ANCHOR_TYPE,
  RESOLVE_TO,
  START_OR_END,
  VISIT_STATUS,
  INSTRUMENT_EVENT,
  TARGETING_ATTRIBUTE,
  AGE_BASIS,
  COHORT_TYPE,
  SAVE_RESUME_MODE,
  EDIT_AFTER_SUBMISSION_MODE,
  DURATION_UNIT,
  completedByToAudienceCohortType,
  completedByToCohortBaselineType,
} from '../constants'
import {
  mapAbsoluteAnchorToApi,
  mapPlatformToLegacy,
  mapRepeatToLegacyRecurring,
  mapEndRulesToApi,
  mapAvailabilityToApi,
} from './legacySchedulePayload'
import { shouldOmitLegacyExpireFromApi } from './scheduleApiPayloadOmissions'
import { createEmptyAvailability } from '../models/createEmptyAvailability'
import { mapRepeatToApi } from './mapRepeatToApi'
import { resolveDeployTimeOfDay } from './resolveDeployTimeOfDay'
import { REPEAT_MODE } from '../constants/repeatTypes'
import { ZERO_OFFSET_API } from '../models/anchorOffset'

const mapVisitPointToApi = visitStatus => {
  if (visitStatus === VISIT_STATUS.completedDate) return 'completed'
  if (visitStatus === VISIT_STATUS.missedDate) return 'missed'
  if (visitStatus === VISIT_STATUS.appointmentScheduledDate) return 'appointment_scheduled'
  return 'target'
}

const mapAnchorTypeToApi = type => {
  if (type === ANCHOR_TYPE.participantStartDate) return 'participant_start'
  if (type === ANCHOR_TYPE.participantLogin) return 'participant_first_login'
  if (type === ANCHOR_TYPE.fixedCalendarDate) return 'absolute'
  return type
}

const mapInstrumentPointToApi = event => {
  if (event === INSTRUMENT_EVENT.completion) return 'completed'
  if (event === INSTRUMENT_EVENT.missed) return 'missed'
  return 'available'
}

const mapOffsetToApi = offset => ({
  value: offset?.amount ?? offset?.value ?? 0,
  unit: offset?.unit || 'days',
  direction: offset?.direction || 'after',
})

const mapAnchorToApi = (anchor, timeOfDay) => {
  const offset = mapOffsetToApi(anchor?.offset)
  const timing_mode = anchor?.timingMode || 'from_start_of_day'
  const type = mapAnchorTypeToApi(anchor?.type)

  if (type === ANCHOR_TYPE.visit) {
    const refs = anchor?.visitRefs?.length
      ? anchor.visitRefs
      : (anchor?.instanceIds?.length ? anchor.instanceIds : [anchor?.instanceId])
          .filter(Boolean)
          .map(visitId => ({ visitId, armId: '' }))
    return {
      type,
      point: mapVisitPointToApi(anchor?.visitStatus),
      visits: refs.map(ref => ({
        visit_id: ref.visitId || ref.visit_id,
        ...(ref.armId || ref.arm_id ? { arm_id: ref.armId || ref.arm_id } : {}),
        label: ref.label,
      })),
      offset,
      timing_mode,
    }
  }

  if (type === ANCHOR_TYPE.visitCycle) {
    return {
      type: 'visit_cycle',
      cycle_id: anchor?.instanceId,
      point: anchor?.startOrEnd === START_OR_END.end ? 'end' : 'start',
      offset,
      timing_mode,
    }
  }

  if (type === ANCHOR_TYPE.instrument) {
    return {
      type,
      instrument_id: anchor?.instanceId,
      point: mapInstrumentPointToApi(anchor?.instrumentEvent),
      offset,
      timing_mode,
    }
  }

  if (type === 'absolute') {
    return {
      ...mapAbsoluteAnchorToApi(anchor, timeOfDay),
      offset: ZERO_OFFSET_API,
      timing_mode,
    }
  }

  const entityTypes = [ANCHOR_TYPE.arm, ANCHOR_TYPE.phase, ANCHOR_TYPE.track, ANCHOR_TYPE.chapter]
  if (entityTypes.includes(type)) {
    const idKey =
      type === ANCHOR_TYPE.arm
        ? 'arm_id'
        : type === ANCHOR_TYPE.phase
          ? 'phase_id'
          : type === ANCHOR_TYPE.track
            ? 'track_id'
            : 'chapter_id'
    const payload = {
      type,
      [idKey]: anchor?.instanceId,
      point: anchor?.startOrEnd === START_OR_END.end ? 'end' : 'start',
      offset,
      timing_mode,
    }
    if (type === ANCHOR_TYPE.phase && anchor?.armId) payload.arm_id = anchor.armId
    return payload
  }

  return {
    type,
    point: anchor?.startOrEnd === START_OR_END.end ? 'end' : 'start',
    offset,
    timing_mode,
  }
}

const mapTargetingToCohort = (targeting = [], audience = {}) => {
  const filterCohort = []
  const siteIds = []

  targeting
    .filter(row => !row.lockedByAnchor && row.value)
    .forEach(row => {
      if (row.attribute === TARGETING_ATTRIBUTE.age) {
        filterCohort.push({
          type: COHORT_TYPE.age,
          filter: {
            min_age: Number(row.value),
            ...(row.maxAge != null ? { max_age: Number(row.maxAge) } : {}),
            basis: row.ageBasis === AGE_BASIS.current ? 'current' : 'enrollment',
          },
        })
        return
      }
      // Arm/phase scope belongs on deploy anchors (visit, arm, phase, etc.), not deploy cohort.
      if (
        row.attribute === TARGETING_ATTRIBUTE.arm ||
        row.attribute === TARGETING_ATTRIBUTE.periodPhase
      ) {
        return
      }
      if (row.attribute === TARGETING_ATTRIBUTE.siteLocation) {
        siteIds.push(row.value)
        return
      }
      if (row.attribute === TARGETING_ATTRIBUTE.language) {
        filterCohort.push({ type: COHORT_TYPE.language, filter: { language: row.value } })
      }
    })

  if (siteIds.length) filterCohort.push({ type: COHORT_TYPE.sites, filter: { site_ids: siteIds } })

  const baseline = {
    type: completedByToCohortBaselineType(audience?.completedBy),
    filter: {},
  }
  return [baseline, ...filterCohort]
}

export const mapPermissionsToApi = (instancePermissions = {}) => {
  const save = instancePermissions.saveResume || {}
  const edit = instancePermissions.editAfterSubmission || {}
  const saveEnabled = save.mode === SAVE_RESUME_MODE.yesMinutes

  const save_and_resume = saveEnabled
    ? {
        enabled: true,
        minutes: Number(save.value) || 30,
        unit: save.unit || DURATION_UNIT.minutes,
      }
    : { enabled: false }

  const editMode = edit.mode || EDIT_AFTER_SUBMISSION_MODE.no
  let edit_after_submission = { enabled: false }
  if (editMode === EDIT_AFTER_SUBMISSION_MODE.untilExpires) {
    edit_after_submission = { enabled: true, until_expires: true }
  } else if (editMode === EDIT_AFTER_SUBMISSION_MODE.noLimit) {
    edit_after_submission = { enabled: true, no_limit: true }
  } else if (editMode === EDIT_AFTER_SUBMISSION_MODE.hours) {
    edit_after_submission = {
      enabled: true,
      hours: Number(edit.value) || 24,
      unit: edit.unit || DURATION_UNIT.hours,
    }
  }

  return { save_and_resume, edit_after_submission }
}

export const toApiSchedule = schedule => {
  const timeOfDay = resolveDeployTimeOfDay(schedule)
  const anchors = schedule?.combineAnchors
    ? (schedule.anchors || []).map(anchor => mapAnchorToApi(anchor, timeOfDay))
    : [mapAnchorToApi(schedule.anchors?.[0], timeOfDay)]
  const cohort = mapTargetingToCohort(schedule.targeting, schedule.audience)
  const platformType = mapPlatformToLegacy(schedule.audience?.platformType || 'both')
  const recurring = mapRepeatToLegacyRecurring(schedule.repeat, schedule.endRules, schedule.availability)
  const end_rules = mapEndRulesToApi(schedule.endRules)
  const availability = mapAvailabilityToApi(schedule.availability || createEmptyAvailability())
  const payload = {
    schedule_id: schedule.scheduleId || schedule.id,
    version: schedule.version || 1,
    platform_type: platformType,
    audience_type: completedByToAudienceCohortType(schedule.audience?.completedBy),
    availability,
    cohort,
    instance_permissions: mapPermissionsToApi(schedule.instancePermissions),
    deploy: {
      combine: schedule?.combineAnchors ? schedule.resolveTo || RESOLVE_TO.latestOf : 'none',
      anchors,
    },
  }

  if (!shouldOmitLegacyExpireFromApi({ deploy: { anchors }, availability })) {
    payload.expire = { never: null }
  }

  payload.repeat = mapRepeatToApi(schedule.repeat)

  const isRepeating =
    schedule.repeat?.mode &&
    schedule.repeat.mode !== REPEAT_MODE.doesNotRepeat &&
    schedule.repeat.mode !== REPEAT_MODE.oncePerAnchorOccurrence

  if (isRepeating && recurring) payload.recurring = recurring
  if (isRepeating && end_rules) payload.end_rules = end_rules

  return payload
}

export const toApiSchedules = schedules => (schedules || []).map(toApiSchedule)

